import React, { useMemo } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { COLORS } from '../constants/theme';
import type { InAppNotification } from '../types';
import { formatEventDate, stripEventDateFromBody } from '../utils/notificationDisplay';

const WIDGET_NOTIFICATION_LIMIT = 10;

interface HomeNotificationsProps {
  readonly notifications: readonly InAppNotification[];
  readonly onNotificationPress?: (notification: InAppNotification) => void;
  readonly onGoToNotifications?: () => void;
}

export const HomeNotifications: React.FC<HomeNotificationsProps> = ({
  notifications,
  onNotificationPress,
  onGoToNotifications,
}) => {
  const visibleNotifications = useMemo(
    () => notifications.slice(0, WIDGET_NOTIFICATION_LIMIT),
    [notifications]
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Notifications</Text>
        {onGoToNotifications ? (
          <TouchableOpacity onPress={onGoToNotifications} activeOpacity={0.8}>
            <Text style={styles.seeAll}>See all</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {visibleNotifications.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>You have no notifications yet.</Text>
        </View>
      ) : (
        <ScrollView
          style={[styles.list, Platform.OS === 'web' ? styles.listWeb : null]}
          contentContainerStyle={styles.listContent}
          nestedScrollEnabled
          showsVerticalScrollIndicator
          keyboardShouldPersistTaps="handled"
        >
          {visibleNotifications.map((notification, index) => {
            const dateLabel = formatEventDate(notification.eventDate || '');
            const isin = notification.actionPayload?.isin?.trim() || '';

            return (
              <TouchableOpacity
                key={notification.id}
                style={[
                  styles.item,
                  index === visibleNotifications.length - 1 && styles.itemLast,
                  !notification.isRead && styles.itemUnread,
                ]}
                activeOpacity={0.8}
                onPress={() => onNotificationPress?.(notification)}
                disabled={!onNotificationPress}
              >
                <Text style={styles.headline} numberOfLines={1}>
                  {dateLabel ? <Text style={styles.dateText}>{`Event date: ${dateLabel}`}</Text> : null}
                  {dateLabel && isin ? <Text style={styles.separator}>{' | '}</Text> : null}
                  {isin ? <Text style={styles.isinText}>{isin}</Text> : null}
                  {(dateLabel || isin) && notification.title ? (
                    <Text style={styles.separator}>{' | '}</Text>
                  ) : null}
                  <Text style={styles.titleText}>{notification.title}</Text>
                </Text>
                <Text style={styles.body} numberOfLines={2}>
                  {stripEventDateFromBody(notification.body, notification.eventDate)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  header: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 16,
    color: COLORS.white,
    fontFamily: 'Montserrat_700Bold',
  },
  seeAll: {
    fontSize: 12,
    color: COLORS.chartSecondary,
    fontFamily: 'Montserrat_600SemiBold',
  },
  emptyState: {
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  emptyText: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_500Medium',
  },
  list: {
    maxHeight: 280,
  },
  listWeb: {
    overflow: 'scroll',
  },
  listContent: {
    flexGrow: 1,
  },
  item: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F4F4F4',
  },
  itemLast: {
    borderBottomWidth: 0,
  },
  itemUnread: {
    backgroundColor: '#F3FBFA',
  },
  headline: {
    fontSize: 13,
    marginBottom: 4,
  },
  dateText: {
    fontSize: 13,
    color: COLORS.chartAccent,
    fontFamily: 'Montserrat_600SemiBold',
  },
  separator: {
    fontSize: 13,
    color: '#9CA3AF',
    fontFamily: 'Montserrat_500Medium',
  },
  isinText: {
    fontSize: 13,
    color: COLORS.primary,
    fontFamily: 'Montserrat_600SemiBold',
  },
  titleText: {
    fontSize: 13,
    color: COLORS.text,
    fontFamily: 'Montserrat_600SemiBold',
  },
  body: {
    fontSize: 12,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_500Medium',
    lineHeight: 17,
  },
});
